'use client';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { BaseLineUsed } from '@/app/components/icons/BaseLineUsed';
import { ProductLineTimestamp } from '@/app/components/icons/ProductLineTimestamp';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { useStore } from '@/app/providers/store';
import type { ClawBillingMode } from '@/app/types/claw';
import { cn, reportEvent } from '@/app/utils';
import { useOpenPurchaseDialog } from '../../../_hook/use-open-purchase-dialog';

const BILLING_MODE_OPTIONS = [
  {
    value: 'claw_pass',
    labelKey: 'claw.chat.billingModeClawPass',
    descKey: 'claw.chat.billingModeClawPassDesc',
    Icon: ProductLineTimestamp,
  },
  {
    value: 'credits',
    labelKey: 'claw.chat.billingModeCredits',
    descKey: 'claw.chat.billingModeCreditsDesc',
    Icon: BaseLineUsed,
  },
] as const;

interface BillingModeControlsProps {
  billingMode?: ClawBillingMode;
  onBillingModeChange: (billingMode: ClawBillingMode) => void;
}

export const BillingModeControls = ({ billingMode, onBillingModeChange }: BillingModeControlsProps) => {
  const { clawPass } = useStore((state) => ({
    clawPass: state.clawPass,
  }));
  const t = useTranslations();
  const openPurchaseDialog = useOpenPurchaseDialog();
  const currentBillingModeOption = BILLING_MODE_OPTIONS.find((option) => option.value === billingMode);

  if (!currentBillingModeOption) return null;

  const CurrentBillingModeIcon = currentBillingModeOption.Icon;
  const handleBillingModeSelect = (nextBillingMode: ClawBillingMode) => {
    reportEvent('claw_element_click', { element_name: 'billing_mode_select', value: nextBillingMode });
    if (nextBillingMode === 'claw_pass' && clawPass?.userVariant === 'free') {
      openPurchaseDialog('claw_page_billing_mode');
      return;
    }
    onBillingModeChange(nextBillingMode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <div className="flex h-8 cursor-pointer items-center gap-1.5 rounded-12 bg-system-white08 light:bg-white px-3 font-semibold light:text-[rgba(18,24,38,0.74)] text-system-white80 text-xs transition-colors hover:bg-system-white12 light:hover:bg-[rgba(18,24,38,0.04)]">
          <CurrentBillingModeIcon className="size-3.5 shrink-0 light:text-[rgba(18,24,38,0.64)] text-system-white80" />
          <span>{t(currentBillingModeOption.labelKey)}</span>
          <ChevronDown className="size-4 shrink-0 light:text-[rgba(18,24,38,0.4)] text-system-white40" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="center"
        className="w-44 rounded-8 bg-[#2c323d] light:bg-white p-1.5 light:shadow-[0_16px_48px_rgba(15,23,42,0.14)] shadow-[0_8px_24px_rgba(0,0,0,0.28)]"
      >
        <DropdownMenuLabel className="mb-0.5 px-0 py-0 light:text-[rgba(18,24,38,0.48)] text-sm text-system-white40">
          {t('claw.chat.billingModeTitle')}
        </DropdownMenuLabel>
        {BILLING_MODE_OPTIONS.map((option) => {
          const OptionIcon = option.Icon;
          return (
            <DropdownMenuItem
              key={option.value}
              className={cn(
                'mb-1 flex items-start gap-1.5 rounded-6 py-1.5 pr-7.5 pl-2 text-left transition-colors hover:bg-system-white16 light:hover:bg-[rgba(18,24,38,0.06)] focus-visible:bg-system-white16 light:focus-visible:bg-[rgba(18,24,38,0.06)] focus-visible:outline-none',
                billingMode === option.value && 'bg-system-white24 light:bg-[rgba(18,24,38,0.08)]',
              )}
              onSelect={() => handleBillingModeSelect(option.value)}
            >
              <div className="flex h-5 items-center justify-center">
                <OptionIcon className="size-4 shrink-0 light:text-[rgba(18,24,38,0.56)] text-system-white80" />
              </div>
              <div className="flex flex-col">
                <span className="whitespace-nowrap font-medium light:text-[#121826] text-sm text-system-white">
                  {t(option.labelKey)}
                </span>
                <span className="whitespace-nowrap light:text-[rgba(18,24,38,0.42)] text-system-white40 text-xs">
                  {t(option.descKey)}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
